import {
    deleteScope,
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryListItem,
    ILibraryListScope,
} from "@/api";
import { MoveToFolderModal } from "@/components/shared/move-to-folder";
import {
    RowActionsMenu,
    type RowAction,
} from "@/components/shared/RowActionsMenu";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    AlertTriangle,
    FolderInput,
    Loader2,
    Pencil,
    Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { RenameScopeDialog } from "./RenameScopeDialog";

interface ScopeActionsMenuProps {
    scope: ILibraryListScope;
    onAction: () => void;
}

export const ScopeActionsMenu = ({
    scope,
    onAction,
}: ScopeActionsMenuProps) => {
    const queryClient = useQueryClient();
    const [renameOpen, setRenameOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const actions = useMemo<RowAction[]>(
        () => [
            {
                key: "rename",
                label: "Rename",
                icon: <Pencil />,
                onSelect: () => setRenameOpen(true),
            },
            {
                key: "move",
                label: "Move",
                icon: <FolderInput />,
                onSelect: () => setMoveOpen(true),
            },
            {
                key: "delete",
                label: "Delete",
                icon: <Trash2 />,
                variant: "destructive",
                onSelect: () => setDeleteOpen(true),
            },
        ],
        []
    );

    const handleSuccess = (close: () => void) => () => {
        close();
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
        queryClient.invalidateQueries({
            queryKey: ["frontend-library-folders"],
        });
        onAction();
    };

    return (
        <>
            <RowActionsMenu actions={actions} />

            <RenameScopeDialog
                open={renameOpen}
                onClose={() => setRenameOpen(false)}
                scope={scope}
                onRenamed={handleSuccess(() => setRenameOpen(false))}
            />

            <MoveToFolderModal
                open={moveOpen}
                onClose={() => setMoveOpen(false)}
                items={[
                    {
                        type: "scope",
                        id: scope.id,
                        name: scope.document_title,
                        status: scope.status,
                        client_name: scope.client_name,
                        client_id: null,
                        industry_name: null,
                        carrier_name: null,
                        clause_checklist_id: scope.clause_checklist_id,
                        clause_checklist_name: scope.clause_checklist_name,
                        item_count: 0,
                        fields_count: null,
                        library_folder_id: scope.library_folder_id,
                        parent_id: null,
                        is_knowledge_base: false,
                        created_at: scope.created_at,
                        updated_at: null,
                        shared_by_organization_name: null,
                        shared_group_name: null,
                    } satisfies ILibraryListItem,
                ]}
                onMoved={handleSuccess(() => setMoveOpen(false))}
            />

            <DeleteScopeDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                scope={scope}
                onDeleted={handleSuccess(() => setDeleteOpen(false))}
            />
        </>
    );
};

// ---------------------------------------------------------------------------
// Delete Dialog — inline shadcn Dialog. Not in Unit 7's external-dialog list
// (the 9 dialogs there are file-level); kept inline because it's bound to
// the menu's deleteScope mutation. Mirrors the Unit 7 delete-dialog template
// (Variant B defensive close + destructive-tinted Alert chrome).
// ---------------------------------------------------------------------------

const DeleteScopeDialog = ({
    open,
    onClose,
    scope,
    onDeleted,
}: {
    open: boolean;
    onClose: () => void;
    scope: ILibraryListScope;
    onDeleted: () => void;
}) => {
    const [error, setError] = useState<string | null>(null);

    const { mutate, isPending } = useMutation({
        mutationFn: () => deleteScope(scope.id),
        onSuccess: () => {
            toast.success("Scope deleted successfully");
            onDeleted();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to delete scope"));
        },
    });

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !isPending) handleClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Scope</DialogTitle>
                </DialogHeader>
                <div
                    data-slot="alert"
                    className="border-destructive/15 bg-destructive/5 flex items-start gap-3 rounded-md border p-3 text-sm"
                >
                    <AlertTriangle className="text-destructive size-4 shrink-0" />
                    <p className="text-foreground">
                        This action cannot be undone. The scope and all
                        associated analysis data will be permanently removed.
                    </p>
                </div>
                <div className="space-y-2 text-sm">
                    <p>Are you sure you want to delete this scope?</p>
                    <p className="break-all">
                        <strong>{scope.document_title}</strong>
                    </p>
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => mutate()}
                        disabled={isPending}
                    >
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
